import { useState, useEffect, useCallback } from 'react';

export interface CartItem {
  variantId: string;
  quantity: number;
}

export function useCart(shopSlug: string) {
  const storageKey = `cart_${shopSlug}`;

  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [couponCode, setCouponCodeState] = useState<string>(() => {
    try {
      return localStorage.getItem(`coupon_${shopSlug}`) || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [storageKey, items]);

  const setCouponCode = useCallback((code: string) => {
    setCouponCodeState(code);
    try {
      if (code) {
        localStorage.setItem(`coupon_${shopSlug}`, code);
      } else {
        localStorage.removeItem(`coupon_${shopSlug}`);
      }
    } catch {
      // ignore
    }
  }, [shopSlug]);

  const addItem = useCallback((variantId: string, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.variantId === variantId);
      if (existing) {
        return prev.map((i) =>
          i.variantId === variantId ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, { variantId, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((variantId: string, quantity: number) => {
    setItems((prev) => {
      if (quantity <= 0) {
        return prev.filter((i) => i.variantId !== variantId);
      }
      return prev.map((i) => (i.variantId === variantId ? { ...i, quantity } : i));
    });
  }, []);

  const removeItem = useCallback((variantId: string) => {
    setItems((prev) => prev.filter((i) => i.variantId !== variantId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCouponCode('');
    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`coupon_${shopSlug}`);
    } catch {
      // ignore
    }
  }, [storageKey, shopSlug, setCouponCode]);

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    items,
    totalQuantity,
    couponCode,
    setCouponCode,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
  };
}
